/**
 * Unit detection and conversion for channel traces.
 *
 * Stored unit strings come from the AiM device / XRK parser and may vary.
 * We normalise to lowercase before matching.
 */

// AiM devices output a wide variety of unit strings. Match generously.
// '.c' / '.f' is how some exports render the degree sign.
const TEMP_C  = new Set(['°c', 'degc', 'celsius', 'c', 'deg.c', '.c']);
const TEMP_F  = new Set(['°f', 'degf', 'fahrenheit', 'f', 'deg.f', '.f']);
const SPEED_KMH = new Set(['km/h', 'kmh', 'kph', 'km/hr', 'kilometres/h', 'kilometers/h']);
const SPEED_MPH = new Set(['mph', 'mi/h', 'miles/h']);
const SPEED_MS  = new Set(['m/s', 'ms-1', 'm/sec']);
const PRES_BAR  = new Set(['bar']);
const PRES_PSI  = new Set(['psi', 'lbf/in2', 'lb/in2']);
const PRES_KPA  = new Set(['kpa', 'kilopascal', 'kilopascals']);
const PRES_MPA  = new Set(['mpa', 'megapascal']);

function normalise(unit) {
  return (unit || '').toLowerCase().trim().replace(/\s+/g, '').replace(/°/g, '°');
}

// Bare single-letter units 'c' and 'f' are ambiguous, so check them last
// only when they stand alone (no surrounding context makes them something else).
function matchesTempC(u) { return TEMP_C.has(u); }
function matchesTempF(u) { return TEMP_F.has(u); }

/** Returns 'temp' | 'speed' | 'pressure' | null */
export function detectCategory(unit) {
  const u = normalise(unit);
  if (!u) return null;
  if (matchesTempC(u) || matchesTempF(u)) return 'temp';
  if (SPEED_KMH.has(u) || SPEED_MPH.has(u) || SPEED_MS.has(u)) return 'speed';
  if (PRES_BAR.has(u) || PRES_PSI.has(u) || PRES_KPA.has(u) || PRES_MPA.has(u)) return 'pressure';
  return null;
}

/**
 * If `unit` is a temperature unit, returns the native scale: 'C' | 'F'.
 * Otherwise null. Used by the video layout editor to decide whether to show
 * the °C/°F selector and which affine transform converts to the chosen unit.
 */
export function tempNativeUnit(unit) {
  const u = normalise(unit);
  if (matchesTempC(u)) return 'C';
  if (matchesTempF(u)) return 'F';
  return null;
}

/**
 * Affine transform { scale, offset } mapping a raw temperature in `fromCorF`
 * ('C'|'F') to a display value in `toCorF`, i.e. display = raw * scale + offset.
 * Identity when the units match. Constants match `convertValue` above.
 */
export function tempAffine(fromCorF, toCorF) {
  if (fromCorF === toCorF) return { scale: 1, offset: 0 };
  if (fromCorF === 'C' && toCorF === 'F') return { scale: 9 / 5, offset: 32 };
  if (fromCorF === 'F' && toCorF === 'C') return { scale: 5 / 9, offset: -160 / 9 };
  return { scale: 1, offset: 0 };
}

/**
 * If `unit` is a pressure unit, returns the canonical native scale:
 * 'bar' | 'psi' | 'kPa' | 'MPa'. Otherwise null. Used by the video layout
 * editor to decide whether to show the pressure selector and which transform
 * converts to the chosen unit.
 */
export function pressureNativeUnit(unit) {
  const u = normalise(unit);
  if (PRES_BAR.has(u)) return 'bar';
  if (PRES_PSI.has(u)) return 'psi';
  if (PRES_KPA.has(u)) return 'kPa';
  if (PRES_MPA.has(u)) return 'MPa';
  return null;
}

// Value-in-unit per 1 bar — the multiplicative constants used by `convertValue`.
const _PRES_PER_BAR = { bar: 1, psi: 14.5038, kPa: 100, MPa: 0.1 };

/**
 * Affine transform { scale, offset } mapping a raw pressure in `fromUnit`
 * ('bar'|'psi'|'kPa'|'MPa') to a display value in `toUnit`, i.e.
 * display = raw * scale + offset. Pressure has no offset (offset is always 0).
 * Identity when the units match. Constants match `convertValue` above.
 */
export function pressureAffine(fromUnit, toUnit) {
  const from = _PRES_PER_BAR[fromUnit];
  const to = _PRES_PER_BAR[toUnit];
  if (!from || !to || fromUnit === toUnit) return { scale: 1, offset: 0 };
  return { scale: to / from, offset: 0 };
}

/** Convert a raw value from its stored unit to the user's preferred unit. */
export function convertValue(value, fromUnit, prefs) {
  if (value == null || isNaN(value)) return value;
  const u = normalise(fromUnit);

  if (matchesTempC(u)) return prefs.temp === 'F' ? value * 9 / 5 + 32 : value;
  if (matchesTempF(u)) return prefs.temp === 'C' ? (value - 32) * 5 / 9 : value;

  if (SPEED_KMH.has(u)) return prefs.speed === 'mph' ? value * 0.621371 : value;
  if (SPEED_MPH.has(u)) return prefs.speed === 'km/h' ? value / 0.621371 : value;
  if (SPEED_MS.has(u))  return prefs.speed === 'km/h' ? value * 3.6 : value * 2.23694;

  // Normalise everything to bar first, then convert to target
  let bar = value;
  if (PRES_PSI.has(u)) bar = value / 14.5038;
  else if (PRES_KPA.has(u)) bar = value / 100;
  else if (PRES_MPA.has(u)) bar = value * 10;

  if (PRES_BAR.has(u) || PRES_PSI.has(u) || PRES_KPA.has(u) || PRES_MPA.has(u)) {
    if (prefs.pressure === 'psi') return bar * 14.5038;
    if (prefs.pressure === 'kPa') return bar * 100;
    return bar;
  }

  return value;
}

/**
 * Convert a distance in feet to the user's preferred distance unit.
 * Distance has no dedicated preference — it follows the speed preference:
 * km/h → meters, mph → feet. Source is feet because all trace charts keep
 * their internal x-axis (`dist_ft`) in feet for the shared zoom/hover bus.
 */
export function convertDistanceFt(ft, prefs) {
  if (ft == null || isNaN(ft)) return ft;
  return prefs?.speed === 'km/h' ? ft * 0.3048 : ft;
}

/** Display unit label for distance, derived from the speed preference. */
export function getDistanceUnit(prefs) {
  return prefs?.speed === 'km/h' ? 'm' : 'ft';
}

/** Returns the display unit label after applying user prefs. */
export function getDisplayUnit(fromUnit, prefs) {
  if (!fromUnit) return '';
  const u = normalise(fromUnit);

  if (matchesTempC(u) || matchesTempF(u)) return prefs.temp === 'F' ? '°F' : '°C';

  if (SPEED_KMH.has(u) || SPEED_MPH.has(u) || SPEED_MS.has(u))
    return prefs.speed === 'km/h' ? 'km/h' : 'mph';

  if (PRES_BAR.has(u) || PRES_PSI.has(u) || PRES_KPA.has(u) || PRES_MPA.has(u)) {
    if (prefs.pressure === 'psi') return 'psi';
    if (prefs.pressure === 'kPa') return 'kPa';
    return 'bar';
  }

  return fromUnit;
}

/**
 * Returns a transform function if the unit is convertible, otherwise null.
 * The returned function maps raw → display value.
 */
export function makeValueTransform(fromUnit, prefs) {
  if (!detectCategory(fromUnit)) return null;
  return (value) => convertValue(value, fromUnit, prefs);
}

const _WIND_DIRS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
function windCardinal(deg) {
  return _WIND_DIRS[Math.round((deg % 360) / 45) % 8];
}

/**
 * Convert a raw weather dict (all values in metric SI units from Open-Meteo)
 * into display-ready strings respecting the user's unit preferences.
 *
 * Returns: { temp, wind, windDir, humidity, pressure, precip, condition }
 */
export function convertWeatherUnits(weather, prefs) {
  if (!weather) return null;

  // Temperature: stored as °C
  const tempVal = weather.temperature_c;
  const temp = tempVal != null
    ? (prefs.temp === 'F'
        ? `${Math.round(tempVal * 9 / 5 + 32)}°F`
        : `${Math.round(tempVal)}°C`)
    : null;

  // Wind speed: stored as km/h
  const windVal = weather.wind_kph;
  const wind = windVal != null
    ? (prefs.speed === 'mph'
        ? `${Math.round(windVal * 0.621371)} mph`
        : `${Math.round(windVal)} km/h`)
    : null;
  const windDir = weather.wind_dir_deg != null
    ? windCardinal(weather.wind_dir_deg)
    : null;

  // Humidity: unitless %
  const humidity = weather.humidity_pct != null
    ? `${weather.humidity_pct}%`
    : null;

  // Pressure: stored as hPa (1 hPa = 0.001 bar = 0.0145038 psi = 0.1 kPa)
  const hpa = weather.pressure_hpa;
  let pressure = null;
  if (hpa != null) {
    if (prefs.pressure === 'psi')     pressure = `${(hpa * 0.0145038).toFixed(1)} psi`;
    else if (prefs.pressure === 'kPa') pressure = `${(hpa * 0.1).toFixed(0)} kPa`;
    else                               pressure = `${(hpa * 0.001).toFixed(3)} bar`;
  }

  // Precipitation: stored as mm
  const precipMm = weather.precipitation_mm ?? 0;
  const precip = prefs.speed === 'mph'
    ? `${(precipMm / 25.4).toFixed(2)}"`
    : `${precipMm.toFixed(1)} mm`;

  return { temp, wind, windDir, humidity, pressure, precip, condition: weather.condition };
}

// ── Persisted unit preferences (localStorage) ────────────────────────────────
const UNIT_PREFS_STORAGE_KEY = 'trackapp:unit_prefs';

export const DEFAULT_UNIT_PREFS = { temp: 'F', speed: 'mph', pressure: 'psi', anchoredTooltip: false, tooltipFontScale: 'medium' };

export const TOOLTIP_FONT_SCALES = { small: 0.85, medium: 1, large: 1.25 };

export function loadUnitPrefs() {
  try {
    const raw = localStorage.getItem(UNIT_PREFS_STORAGE_KEY);
    return raw ? { ...DEFAULT_UNIT_PREFS, ...JSON.parse(raw) } : { ...DEFAULT_UNIT_PREFS };
  } catch {
    return { ...DEFAULT_UNIT_PREFS };
  }
}

export function saveUnitPrefs(prefs) {
  localStorage.setItem(UNIT_PREFS_STORAGE_KEY, JSON.stringify(prefs));
}
